import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { notificationFilterById } from "../../../../../actions/Utilities/NotificationFilter";
import AddButton from "../../../../../components/AddButton";
import ConfirmBoxDelete from "../../../../../components/ConfirmBoxDelete";
import EditButton from "../../../../../components/EditButton";
import MaterialTableTS from "../../../../../components/tables/MaterialTableTS";
import { toast } from "../../../../../components/toastNotification";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../../PermissionsConstants";

import {
  addStateWarrantyCoverage,
  selectedRowsStateWarrantyCoverage,
  editStateWarrantyCoverage,
  warrantyCoverageFilterText,
  rowEditWarrantyCoverage,
  warrantyCoverageFetching,
  warrantyCoverageReFetching,
} from "./../atoms/atoms";
import WarrantyCoverageForm from "../form/WarrantyCoverageForm";
import useGetWarrantyCoverage from "./../hooks/useGetWarranty";
import useDeleteWarranty from "../hooks/useDeleteWarranty";
import { IWarrantyCoverage, IWarrantyCoverageTableData } from "../types";
import Timer from "components/Timer";
import TableFilters from "components/TableFilters";

export const WarrantyCoverageTable = () => {
  const { data: directData, refetch: reFetch } = useGetWarrantyCoverage();

  const BulkDelete = useDeleteWarranty();

  const [actionAddState, setActionAddState] = useRecoilState(
    addStateWarrantyCoverage
  );
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStateWarrantyCoverage
  );
  const [actionEditState, setActionEditState] = useRecoilState(
    editStateWarrantyCoverage
  );
  const [filterTextState, setFilterTextState] = useRecoilState(
    warrantyCoverageFilterText
  );
  const setEditRowData = useSetRecoilState(rowEditWarrantyCoverage);
  const userPermissions = useRecoilValue(permissionsSelector);
  const [isFetching, setIsFetching] = useRecoilState(warrantyCoverageFetching);
  const isReFetching = useRecoilValue(warrantyCoverageReFetching);

  const [tableData, setTableData] = useState<IWarrantyCoverage[]>([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [tableFilteredData, setTableFilteredData] = useState<
    IWarrantyCoverage[]
  >([]);

  const addPermission =
    PERMISSIONS.permission_utilities_securityconfiguration_warrantycoverage_add;
  const deletePermission =
    PERMISSIONS.permission_utilities_securityconfiguration_warrantycoverage_delete;
  const editPermission =
    PERMISSIONS.permission_utilities_securityconfiguration_warrantycoverage_edit;

  const Loader = async () => {
    setTableData(directData?.data.map((data) => ({ ...data })));
    await Timer();
    setIsFetching(false);
  };

  useEffect(() => {
    reFetch();
  }, [isReFetching]);

  useEffect(() => {
    if (directData?.status === 400) {
      toast.fire("Server Error", directData?.data.Message, "error");
      setTableData([]);
      setIsFetching(false);
    } else {
      Loader();
    }
  }, [directData]);

  let location = useLocation();
  let recordIdFromRecent;

  const [recent, setRecent] = useState("");
  if (location.state !== null) {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      notificationFilterById(
        recordIdFromRecent.recordId,
        recordIdFromRecent.moduleName,
        recordIdFromRecent.subModuleName
      ).then((result: IWarrantyCoverage[] | undefined) => {
        if (typeof result !== "object") {
          toast.fire("Failed", result, "error");
        } else {
          setTableFilteredData(result);
          setRecent(result[0].warrantyName);
        }
      });
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
    setSelectedRows([]);
  }, [recordIdFromRecent]);

  const filtereditems =
    tableData &&
    tableData?.filter(
      (item) =>
        item.warrantyName &&
        item.warrantyName
          .toLowerCase()
          .includes(filterTextState?.trim().toLowerCase())
    );
  useEffect(() => {
    if (!recent) {
      setTableFilteredData(filtereditems);
    }
  }, [filterTextState]);

  const handleAdd = () => {
    setActionAddState(true);
  };

  const handleSelectedRowsChange = (rows: IWarrantyCoverageTableData[]) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData: IWarrantyCoverageTableData) => {
    delete rowData.tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
  };

  const handleDelete = async () => {
    setIsFetching(true);
    const ids = selectedRows && selectedRows.map((row) => row.warrantyId);
    BulkDelete(ids);
    setSelectedRows([]);
    setRecent("");
    setOpenModalDelete(false);
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${rowsCount > 1 ? "Warranty Coverages" : "Warranty Coverage"
      }?`}</div>
  );

  const matColumns = [
    {
      title: "Warranty Coverage Name",
      field: "warrantyName",
      customSort: (a, b) =>
        a.warrantyName && b.warrantyName
          ? a.warrantyName.trim().localeCompare(b.warrantyName.trim())
          : a.warrantyName
            ? 0
            : -1,
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
            ? 0
            : -1,
    },
    {
      title: "Created Date",
      field: "createdDate",
      type: "date" as "date",
    },
    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
            ? 0
            : -1,
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date" as "date",
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData?.isActive ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      align: "center" as "center",
      hidden: !userPermissions.includes(editPermission),
      sorting: false,
      render: (rowData) => {
        return (
          <div className="flex items-center place-content-center">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="bg-white p-4">
      {actionAddState || actionEditState ? (
        <WarrantyCoverageForm setRecent={setRecent} />
      ) : (
        <>
          <div className="flex justify-between text-xs items-center w-full">
            <TableFilters
              defaultText={filterTextState}
              textInputName={"WarrantyCoverageName"}
              columnDetails={"Warranty Coverage Name"}
              filterText={setFilterTextState}
              recentText={recent}
              setRecent={setRecent}
              disabledButton={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              disabledTextBox={
                selectedRows !== undefined && selectedRows.length > 0
              }
            />

            <div className="">
              {userPermissions.includes(addPermission) ? (
                <AddButton
                  buttonText="New Warranty Coverage"
                  onClick={() => handleAdd()}
                  disabled={
                    actionEditState ||
                    (selectedRows !== undefined && selectedRows.length > 0)
                  }
                />
              ) : (
                ""
              )}
            </div>
          </div>
          <div>
            {userPermissions.includes(deletePermission) ? (
              <div className="text-secondary italic text-xs3 my-2">
                <p className="font-bold italic inline-block mr-1">Note:</p>
                <span>Tick the box to deactivate some data.</span>
              </div>
            ) : (
              <div className="mb-4"></div>
            )}
          </div>
          <div className="">
            <MaterialTableTS
              data={
                isFetching
                  ? []
                  : filterTextState === "" && recent === ""
                    ? tableData
                    : filterTextState === ""
                      ? tableFilteredData
                      : filtereditems
              }
              showTitle={true}
              columns={matColumns}
              isLoading={isFetching}
              onSelectionChange={handleSelectedRowsChange}
              selectedRows={selectedRows}
              selection={
                !actionEditState &&
                !actionAddState &&
                userPermissions.includes(deletePermission)
              }
              setConfirmModalOpen={setOpenModalDelete}
            />
          </div>

          <ConfirmBoxDelete
            ModalTitle="Deactivate Warranty Coverage"
            qConfirmation={deleteMessage}
            isOpen={openModalDelete}
            onClose={() => {
              setOpenModalDelete(false);
            }}
            onClickFunction={handleDelete}
            cancelButtonRef={undefined}
          />
        </>
      )}
    </div>
  );
};
export default WarrantyCoverageTable;
